import { AuthProvider } from "@kalyzee/kast-web-sdk";
import { useCallback, useMemo } from "react";
import Storage, { TokenType } from "../../helpers/storage";
import { Tokens } from "@kalyzee/kast-web-sdk/http";
import { useSdkContext } from "../../contexts/sdk/context";
import { getGlobalQuery, getGlobalQueryLabel, NavigateGlobalQuery } from "../../helpers/navigation";
import { clearSessionEphemeral, sessionIsEphemeral } from "../../helpers/session";

export const AppAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const sdkCtx = useSdkContext();
  return (
    <AuthProvider
      sdk={sdkCtx.client}
      defaultTokens={useMemo(() => {
        const url = new URL(window.location.href);
        const urlToken = getGlobalQuery(url.href, NavigateGlobalQuery.TOKEN);
        const urlRefreshToken = getGlobalQuery(url.href, NavigateGlobalQuery.REFRESH_TOKEN);

        if (urlToken || urlRefreshToken) {
          url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.TOKEN));
          url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.REFRESH_TOKEN));
          window.history.replaceState({}, '', url.href);
        }

        let token: string | undefined = undefined;
        let refreshToken: string | undefined = undefined;

        if (urlToken) {
          token = urlToken;
          refreshToken = urlRefreshToken || undefined;
        } else {
          token = Storage.getToken(TokenType.Normal) || undefined;
          refreshToken = Storage.getToken(TokenType.Refresh) || undefined;
        }

        return token ? { token, refreshToken } : undefined;
      }, [])}
      onTokens={useCallback((tokens?: Tokens) => {
        // Session end: next session decides its own persistence policy.
        if (!tokens) clearSessionEphemeral();
        // Guest sessions live in memory only — never persist their tokens
        // (a reload must end them). Also make sure nothing lingers in storage.
        if (sessionIsEphemeral()) {
          Storage.setToken(undefined, TokenType.Normal);
          Storage.setToken(undefined, TokenType.Refresh);
          return;
        }
        Storage.setToken(tokens?.token, TokenType.Normal);
        Storage.setToken(tokens?.refreshToken, TokenType.Refresh);
      }, [])}
    > 
      {children}
    </AuthProvider>
  );
}